import React, { useCallback, useContext, useEffect, useState } from 'react';
import styles from '@/common/containers/TaskDetails/styles.less';
import InfoIcon from '@mui/icons-material/Info';
import cx from 'classnames';
import TextField from '@mui/material/TextField';
import { DetailsContext } from '@/common/containers/TaskDetails/components/DetailsProvider';
import { Notification } from '@/common/utils/index';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import debounce from '@/common/utils/debounce';
import { NumberFormatInput } from '@/common/components/Inputs/index';
import Tooltip from '@/common/utils/bbParser/Tooltip';
import { useSubmitAnswersMutation } from '@/entities/question';
import { useLocalizedField } from '@/common/hooks/index';
import { useTranslation } from 'react-i18next';

const SUB_LABEL = {
  Value: 'assessed/estimated',
  'Proceeds from Sale': 'if asset was sold',
};

const TOOLTIPS_ASSETS = {
  title: 'tip142',
  value: 'tip137',
  debt: 'tip138',
  credit: 'tip139',
  expenses: 'tip140',
  'sale proceeds': 'tip141',
};

const TOOLTIPS_LIABILITIES = {
  title: 'tip143',
  debt: 'tip144',
  credit: 'tip145',
  expenses: 'tip146',
};

const AssetValues = ({ type }) => {
  const { t } = useTranslation();
  const decedentId = useCurrentDecedentId();
  const { assetValues } = useContext(DetailsContext);
  const getLocalizedField = useLocalizedField();
  const {
    options,
    taskStepId,
    answerId,
    value: savedValues,
  } = assetValues || {};
  const [values, setValues] = useState({});
  const submitAnswersMutation = useSubmitAnswersMutation();
  const debouncedSubmit = useCallback(
    debounce((payload) => {
      submitAnswersMutation
        .mutateAsync({
          answers: [payload],
          decedentId,
          section: 'main',
          sectionType: 'Task',
        })
        .catch(() => Notification.error('Error during saving values'));
    }, 300),
    [decedentId, submitAnswersMutation]
  );

  useEffect(() => {
    if (savedValues) {
      setValues(savedValues);
    }
  }, [savedValues]);

  const updateValues = ({ target: { value, name } }) => {
    const nextValues = { ...values, [name]: value };
    setValues(nextValues);
  };

  const onFocusLost = useCallback(() => {
    const action = !answerId ? 'create' : 'update';

    const payload = {
      taskStepId,
      action,
      value: values,
      ...(answerId && { answerId }),
    };
    debouncedSubmit(payload);
  }, [values, answerId, debouncedSubmit, taskStepId]);

  const tooltips =
    type === 'liabilities' ? TOOLTIPS_LIABILITIES : TOOLTIPS_ASSETS;
  const titleTooltip = {
    [(type === 'liabilities' ? TOOLTIPS_LIABILITIES : TOOLTIPS_ASSETS)
      .title]: true,
  };
  if (!taskStepId) return null;
  return (
    <div className={styles.infoBlock}>
      <div className={styles.infoBlockHeader}>
        <h5 className={styles.infoBlockHeaderTitle}>
          {type === 'liabilities'
            ? t('Liability Values')
            : t('Asset Values')}
        </h5>
        <Tooltip {...titleTooltip} placement="left-end">
          <InfoIcon />
        </Tooltip>
      </div>
      <div className={cx(styles.infoBlockBody, styles.assetValues)}>
        {options.map((option) => {
          const { key } = option;
          const label = getLocalizedField(option, 'label');
          const placeholder = getLocalizedField(option, 'placeholder');
          const [label1] = label.split(':');
          const tooltip = tooltips[label1.toLowerCase()];
          return (
            <div key={key} className={styles.assetValuesItem}>
              <div className={styles.assetValuesItemLabels}>
                <span>{label1}:</span>
                {SUB_LABEL[label1] && <span>{SUB_LABEL[label1]}</span>}
              </div>
              <div className={styles.assetValuesItemInputWrap}>
                <TextField
                  onChange={updateValues}
                  value={values[key] || ''}
                  size="small"
                  name={key}
                  placeholder={placeholder}
                  InputProps={{
                    inputComponent: NumberFormatInput,
                  }}
                  onBlur={onFocusLost}
                  disabled={submitAnswersMutation.isPending}
                />
                <Tooltip {...{ [tooltip]: true }} placement="left-end">
                  <InfoIcon />
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssetValues;
